// ============================================================================
// CREATE QUOTE — the rep's handoff from the Weight Designer into the Quote Workstation.
//
// Ryan 2026-08-30: *"if a rep is making a design in the portal and they click on the 'Request
// Quote' tab (needs to be renamed Create Quote and it needs to have the same 3 follow-up options
// added to the portal version of the WD that we have in the GLD) then it will automatically link
// the file to the chain."*
//
// ⛔ THIS IS NOT THE BUYER'S PATH RENAMED. Step 4 collects a CUSTOMER's details and posts to
// `/wd-quote`, which mints a LEAD. A rep is not filing a lead; they are starting a quote they will
// price themselves. So this is a second, rep-only exit from the same step, and the customer form
// stays exactly where it is — a rep taking a request over the phone still wants it.
//
// ⛔ AND IT DELIBERATELY REUSES THE GLD'S CONTRACT RATHER THAN INVENTING ONE. The Gym Layout
// Designer has handed off through `localStorage['rep_workstation_import']` since 2026-05-21, and
// `workstation-boot.js` already: reads `{mode, baseRef, targetRef, designRef, designId, items}`,
// runs the three modes, and attaches a one-shot `rq-ws:saved` listener that POSTs
// `/quote-link-design` once the quote ref exists. Every word of Ryan's ask is already implemented
// on the receiving side — what was missing was a sender. Writing a second handoff shape would mean
// two things to keep in step, and the auto-link is the half that would rot.
//
// ⚠ `items: []` USED TO BE THE WHOLE STORY, AND IS NOW ONLY HALF OF IT. Until 2026-08-31 this
// handed over a design and nothing else, because the house line had no built products and Ryan
// chose that over pre-creating $0 lines. Seven of those lines now exist with real prices, so the
// payload also carries `weights`: the order in the designer's own vocabulary, `category|kind` plus
// pounds, which `workstation-boot.js` prices at its end. `items` stays an empty array rather than
// being deleted because it is the GLD's field and the receiver still reads it; the two merge.
//
// ⛔ AND IT NOW NAMES `designType` / `designStore`. The auto-link this module was built around had
// never once worked: the receiver POSTed `/quote-link-design` with neither field, the worker's
// `type` default is a falsy fallthrough to 'floor-plan' which then forces the `design:` keyspace,
// and a weight design lives at `wd-design:`. Every attempt 404'd into a `console.warn`. Naming both
// is the fix, and the GLD keeps working untouched because it sends neither and wants the defaults.
// ============================================================================

import { h } from './dom.js';
import { openModal } from './modal.js';
import { isRepMode } from '../rep-mode.js';
import { portalApi as api, portalSession, isExternalPortalUser } from '../portal-api.js';
import { orderedForQuote } from './quote-summary.js';

/** The key `workstation-boot.js` reads on load, then immediately removes. Not ours to rename. */
const IMPORT_KEY = 'rep_workstation_import';
const WORKSTATION_URL = '/pages/rep-quote-workstation';

/**
 * Is the handoff available at all?
 *
 * ⚠ `isRepMode()` is NOT a permission check (see rep-mode.js) — the portal's own auth is. This
 * asks whether the surrounding page can receive the handoff: off the portal there is no
 * Workstation to open and no session to save a design against.
 */
export function canCreateQuote() {
  return isRepMode() && typeof window !== 'undefined' && !!window.RepPortal
    && !isExternalPortalUser(); // CUSTOM_PORTALACCESS_BY_CLAUDE — quoting is team work
}

/**
 * A quote links to a SAVED design, so an unsaved one is named and saved first.
 *
 * The GLD does exactly this (`_showSaveDesignPrompt` before the mode picker) and for the same
 * reason: `/quote-link-design` verifies the design record exists, so handing off an unsaved design
 * would produce a quote with nothing attached — the one outcome this feature exists to prevent.
 */
async function ensureSaved(ctx) {
  const cur = (ctx.currentDesign && ctx.currentDesign()) || null;
  if (cur && cur.id && cur.ref) return cur;

  const nameInput = h('input', {
    class: 'wd-qinput', type: 'text', maxlength: '80',
    placeholder: 'e.g. Northgate HS — competition set',
  });
  const err = h('div', { class: 'wd-qerror', hidden: true });
  const choice = await openModal({
    mount: ctx.el,
    title: 'Name this design first',
    body: h('div', { class: 'wd-cq' },
      h('p', { class: 'wd-cq-copy' },
        'A quote links to a saved design, so this one needs a name and a reference number before it can go to the Workstation.'),
      nameInput, err),
    actions: [
      { id: 'go', label: 'Save & continue', variant: 'primary' },
      { id: 'no', label: 'Cancel', variant: 'ghost' },
    ],
  });
  if (choice !== 'go') return null;

  const name = String(nameInput.value || '').trim();
  if (!name) { ctx.status?.error?.('Give the design a name so it can be saved.'); return null; }
  const envelope = ctx.shareEnvelope?.();
  if (!envelope) { ctx.status?.error?.('There is no design on screen to save.'); return null; }

  const r = await api('/wd-design-save', {
    method: 'POST',
    body: { name, design: envelope, meta: { product: ctx.state?.ui?.product || '' } },
  });
  if (!r || !r.ok) { ctx.status?.error?.((r && r.error) || 'Could not save that design.'); return null; }
  const rec = { id: r.id, ref: r.ref, name };
  // Tell the designs menu, or the rep's next "Save" mints a SECOND design instead of updating this
  // one — the same handshake project-push uses.
  ctx.adoptSavedDesign?.(rec);
  return rec;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — ⛔ THE PICKER'S INTELLIGENCE, brought up to
   the Gym Layout Designer's. The three modes and their wording were already mirrored faithfully;
   what this list showed was not. It offered a column of BARE REFERENCE STRINGS, unsorted, searchable
   by reference only, from every rep's quotes. Ryan asked for the WD to "operate in the same way",
   and this is the half of that which is not cosmetic: a rep cannot pick the right chain out of a
   list that never says whose it is. */

/** Strip the revision letter: `JW-1216-B` → `JW-1216`. A chain is named by its base.
    ⚠ `[A-Z]+`, not `[A-Z]`. Past `-Z` a chain continues `-AA`, and a single-letter pattern left
    `JW-1216-AA` grouped under `JW-1216-A` — a different chain. */
function baseOf(ref) {
  return String(ref || '').replace(/-([A-Z]+)$/, '');
}

/** `Date.parse` on a missing timestamp is NaN, which sorts unpredictably. Absent sorts oldest. */
function parseTs(q) {
  const t = Date.parse((q && (q.updatedAt || q.createdAt)) || '');
  return Number.isFinite(t) ? t : 0;
}

/* A residence has no company name worth showing, and these are what the field actually contains
   when somebody had to type something. Fall through to the contact's own name instead. */
const RESIDENCE = new Set(['', 'residence', 'na', 'n/a', 'naresidence', 'home', 'homegym']);
function displayNameFor(q) {
  const org = String((q && q.customerCompany) || '').trim();
  if (org && !RESIDENCE.has(org.toLowerCase().replace(/[^a-z/]/g, ''))) return org;
  return String((q && q.customer) || '').trim() || 'Unknown';
}

/* ⛔ SOMEBODY ELSE'S QUOTE IS NOT A CHAIN THIS DESIGN BELONGS IN. The worker already scopes a
   non-admin to their own quotes, so this changed nothing for most reps — but an ADMIN was being
   offered every rep's chain with nothing on the row to say so, and appending a design to the wrong
   deal is not a mistake the rep can see afterwards.
   ⚠ Fails OPEN: with no session or an unattributable reference we cannot tell, and hiding a quote
   the rep does own is worse than showing one they do not. */
const GENERIC_PREFIX = /^(QUOTE|PLAN|WEIGHT|EQLIST)$/;
function ownedByMe(q, me) {
  if (!me) return true;
  const tagged = String((q && q.rep) || '').trim().toUpperCase();
  if (tagged) return tagged === me;
  const m = String((q && q.reference) || '').match(/^([A-Z]{2,4})-(?:BSFS-)?\d+-[A-Z]+$/);
  if (!m || GENERIC_PREFIX.test(m[1])) return true;
  return m[1] === me;
}

function myInitials() {
  try {
    const sess = portalSession();
    return String((sess && (sess.repCode || sess.code || sess.rep)) || '').trim().toUpperCase();
  } catch { return ''; }
}

/**
 * The three follow-up options, worded as the GLD words them so a rep meets one vocabulary.
 *
 * ⛔ INLINE, NOT A MODAL (Ryan 2026-09-01): *"Skip the create quote button. Just have the 3 options
 * right there on the screen under the quantity selector."* It used to take a button, to open a
 * dialog, to reach three choices — on a screen with room for them. The dialog earns its place in
 * the Gym Layout Designer because the GLD's entry point is a step TAB with no panel of its own.
 * Here there is a panel, so the options live in it.
 *
 * ⚠ A COMMIT BUTTON REMAINS, and it is not the button that was removed. That one opened a dialog;
 * this one acts on a choice already made and visible, and it is disabled until that choice is
 * answerable. Without it a mis-click on a chain row opens a Workstation tab against the wrong deal,
 * which is not something a rep can take back.
 *
 * Calls `onPick({mode, baseRef, targetRef})` when the rep commits. Returns the element.
 */
function buildChooser(ctx, onPick) {
  let mode = 'new';
  let baseRef = null;
  let targetRef = null;
  let quotes = null;

  const err = h('div', { class: 'wd-qerror', hidden: true });
  const search = h('input', { class: 'wd-qinput', type: 'text', placeholder: 'Search reference, company or contact…' });
  const list = h('div', { class: 'wd-cq-list' });
  const chains = h('div', { class: 'wd-cq-chains', hidden: true }, search, list);

  /** Two lines per row: who it is and which reference, then what picking it will do. */
  function row(ref, on, line1, line2, pending) {
    return h('button', {
      type: 'button',
      class: 'wd-cq-ref' + (on ? ' is-on' : ''),
      onClick: () => {
        if (mode === 'revision') baseRef = ref; else targetRef = ref;
        err.hidden = true;
        rows(search.value);
      },
    },
      h('span', { class: 'wd-cq-ref__t' }, line1,
        pending ? h('span', { class: 'wd-cq-pill' }, 'Pending') : null),
      line2 ? h('span', { class: 'wd-cq-ref__s' }, line2) : null);
  }

  /* Hoisted, so the two renderers below can call it though `go` is built after them. Both only
     ever RUN after `paintOpts()` at the foot of this function, by which point `go` exists. */
  function syncGo() {
    const ready = mode === 'new'
      || (mode === 'revision' && !!baseRef)
      || (mode === 'append' && !!targetRef);
    go.disabled = !ready;
    go.classList.toggle('is-disabled', !ready);
  }

  function rows(filter) {
    syncGo();
    /* ⛔ THREE STATES, NOT TWO. `quotes` is null while loading, false when the fetch FAILED, and an
       array otherwise. Collapsing a failure into `[]` told the rep "no quotes of your own to build
       on yet" — a confident claim about their data, made when we know nothing about it. */
    if (quotes === false) {
      list.textContent = 'Could not load your quotes. Close this and try again.';
      return;
    }
    if (!Array.isArray(quotes)) { list.textContent = 'Loading your quotes…'; return; }
    const f = String(filter || '').trim().toLowerCase();
    const hit = (...parts) => !f || parts.some((p) => String(p || '').toLowerCase().indexOf(f) !== -1);

    if (mode === 'revision') {
      /* One row per CHAIN. The rep is choosing which conversation this design joins, so the row has
         to say whose it is and what letter they will get. */
      const byBase = new Map();
      for (const q of quotes) {
        const ref = q && q.reference;
        if (!ref || !/-[A-Z]+$/.test(ref)) continue;   // no letter = not a chain
        const base = baseOf(ref);
        const g = byBase.get(base) || { base, letters: new Set(), latest: null, q };
        g.letters.add(ref.slice(base.length + 1));
        if (!g.latest || parseTs(q) > parseTs(g.latest)) { g.latest = q; g.q = q; }
        byBase.set(base, g);
      }
      const shown = Array.from(byBase.values())
        .filter((g) => hit(g.base, g.q && g.q.customerCompany, g.q && g.q.customer))
        .sort((a, b) => parseTs(b.latest) - parseTs(a.latest))
        .slice(0, 50);
      if (!shown.length) {
        list.textContent = f ? `No chains match “${filter}”.` : 'No quote chains of your own to build on yet.';
        return;
      }
      list.replaceChildren(...shown.map((g) => {
        const letters = Array.from(g.letters).sort();
        const last = letters[letters.length - 1] || 'A';
        const next = last.length === 1 && last !== 'Z'
          ? String.fromCharCode(last.charCodeAt(0) + 1) : last + 'A';
        const n = letters.length;
        return row(g.base, baseRef === g.base,
          `${displayNameFor(g.q)} — ${g.base}`,
          g.latest && g.latest.isPending
            ? `Pending deal -${last}, this design will fill it`
            : `Latest -${last} (${n} revision${n === 1 ? '' : 's'}) → next: -${next}`,
          !!(g.latest && g.latest.isPending));
      }));
      return;
    }

    // 'append' picks one exact quote, so the row names the quote rather than the chain.
    const shown = quotes
      .filter((q) => q && q.reference)
      .filter((q) => hit(q.reference, q.customerCompany, q.customer, q.customerEmail))
      .sort((a, b) => parseTs(b) - parseTs(a))
      .slice(0, 50);
    if (!shown.length) {
      list.textContent = f ? `No quotes match “${filter}”.` : 'No quotes of your own to append to yet.';
      return;
    }
    list.replaceChildren(...shown.map((q) => {
      const org = displayNameFor(q);
      const contact = String(q.customer || '').trim();
      return row(q.reference, targetRef === q.reference,
        `${org} — ${q.reference}`,
        contact && contact !== org ? contact : '',
        !!q.isPending);
    }));
  }

  async function ensureQuotes() {
    if (quotes !== null) return;
    try {
      const r = await api('/rep-quotes?status=all&limit=200');
      // Defensive: this endpoint is shared with the dashboard and its envelope has changed before.
      const all = (r && Array.isArray(r.quotes)) ? r.quotes : [];
      const me = myInitials();
      quotes = all.filter((q) => ownedByMe(q, me));
    } catch {
      quotes = false;   // distinct from [] — see `rows`
    }
    rows(search.value);
  }

  const OPTIONS = [
    ['new', 'Brand new quote', 'Start a fresh quote with a new reference number.'],
    ['revision', 'New revision in an existing chain', 'Pick a base reference and the next letter is created from this design.'],
    ['append', 'Append to an existing quote', 'Add this design to a quote you have already begun.'],
  ];
  const optBtns = new Map();
  function paintOpts() {
    OPTIONS.forEach(([id]) => optBtns.get(id).classList.toggle('is-on', mode === id));
    chains.hidden = mode === 'new';
    syncGo();
    if (mode !== 'new') { rows(search.value); void ensureQuotes(); }
  }

  const body = h('div', { class: 'wd-cq' },
    ...OPTIONS.map(([id, label, sub]) => {
      /* The SAME glass classes the pane-nav buttons wear, not a copy of their declarations — so
         the fill, the border and the radius stay one decision. Ryan asked for these three to read
         as siblings of Back and Create Quote, which they now sit between. */
      const b = h('button', {
        type: 'button',
        class: 'wd-btn wd-panenav__btn wd-flat-glass wd-flat-glass--flush wd-cq-opt',
        onClick: () => { mode = id; err.hidden = true; paintOpts(); },
      }, h('span', { class: 'wd-cq-opt__x' },
        h('span', { class: 'wd-cq-opt__t' }, label),
        h('span', { class: 'wd-cq-opt__s' }, sub)));
      optBtns.set(id, b);
      return b;
    }),
    chains, err);

  const go = h('button', {
    type: 'button',
    class: 'wd-btn wd-panenav__btn wd-flat-glass wd-flat-glass--flush wd-cq-btn',
    onClick: () => { if (!go.disabled) onPick({ mode, baseRef, targetRef }); },
  }, 'Create Quote');

  search.addEventListener('input', () => rows(search.value));
  paintOpts();
  /* ⛔ THE BUTTON IS HANDED OUT, NOT MOUNTED (Ryan 2026-09-01: *"Place the create quote button on
     the footer bar, next to the 'BACK Customize Design' button, on the right half"*). The pane's
     nav footer already has a two-column grid whose forward cell takes whatever `footerAction` hands
     it — the same seam the buyer's Submit button uses. So the chooser returns the control rather
     than rendering it, and `product-step.js` decides where it lives.
     ⚠ A node cannot be in two places: mounting it here as well would MOVE it out of the footer the
     moment this section re-rendered. That is the trap `quoteBtn`'s own note records. */
  return { el: body, action: go };
}

/**
 * The whole handoff: save if needed, pick a mode, write the payload, open the Workstation.
 *
 * @param {Object} ctx
 * @param {{set?: Array, quantities?: Object}} [order] What the rep counted on step 4. Passed in
 *        rather than re-derived because `quantities` lives in `createProductStep`'s closure and is
 *        the only capture on that tab that exists nowhere else. Omit it and the handoff still
 *        works, carrying the design and no lines, which is what it did before 2026-08-31.
 * @returns {Promise<boolean>} true once the Workstation has been opened.
 */
/** @returns {{el: HTMLElement, action: HTMLButtonElement}} the options, and the control that commits them. */
export function createQuoteChooser(ctx, getOrder) {
  return buildChooser(ctx, (picked) => { void runHandoff(ctx, getOrder && getOrder(), picked); });
}

async function runHandoff(ctx, order, picked) {
  /* ⚠ THE SAVE HAPPENS ON COMMIT, not on render. The design has to exist before a quote can link
     to it, and prompting for a name the moment a rep reaches step 4 would ask for one from every
     rep who is only looking. Same order the Gym Layout Designer uses, moved to the later moment
     because the choice is now always on screen. */
  const design = await ensureSaved(ctx);
  if (!design) return false;

  const weights = orderedForQuote(order && order.set, order && order.quantities);
  const lineCount = weights.reduce((n, g) => n + g.lines.length, 0);

  const payload = {
    mode: picked.mode,
    baseRef: picked.baseRef || null,
    targetRef: picked.targetRef || null,
    designRef: design.ref,
    designId: design.id,
    /* ⛔ NAMED, NOT DEFAULTED. `/quote-link-design` reads `type` and `store` to pick which of three
       keyspaces to look the design up in, and its `type` default is a falsy fallthrough to
       'floor-plan', which then FORCES store to 'design:'. A weight design saved by
       `/wd-design-save` lives at `wd-design:{id}`, so omitting these two strings is not a
       harmless default: it is a guaranteed 404 that `workstation-boot.js` swallows into a
       console.warn. That is exactly what shipped on 2026-08-31 and never once linked a design. */
    designType: 'weights',
    designStore: 'wd-design',
    items: [],
    /* The order, catalogue-agnostic: `category|kind` plus pounds. `workstation-boot.js` turns each
       line into a priced Quote Writer row by resolving the product to a Shopify handle at its end.
       See `orderedForQuote` for why the mapping deliberately does not live in this app. */
    weights,
  };
  try {
    localStorage.setItem(IMPORT_KEY, JSON.stringify(payload));
  } catch (_) {
    /* A full or blocked localStorage is the one failure that must not open a Workstation which
       would then quietly start an UNLINKED quote — the exact outcome this feature removes. */
    ctx.status?.error?.('This browser would not let us hand the design over. Check private-mode settings and try again.');
    return false;
  }
  /* ⚠ AN EMPTY HANDOFF IS A REAL OUTCOME AND IT IS SAID OUT LOUD. A rep who never opened the
     quantity grid (rep mode never shows it) has counted nothing, so there is nothing to price and
     `orderedForQuote` correctly returns []. Opening a blank Workstation without a word would read
     as the feature failing, when it is the design working exactly as asked. */
  /* ⛔ ZERO QUANTITIES IS A FIRST-CLASS ROUTE, not a warning (Ryan 2026-09-01: *"ensure that it's
     acceptable if reps keep the quantities at 0 and then just fill that out in the QW"*). Nothing
     gates on a count: the commit button asks only whether the MODE is answerable,
     `orderedForQuote` returns [] without complaint, and `workstation-boot.js` honours the chosen
     mode with an empty item list. So the message SAYS WHAT HAPPENS NEXT rather than reporting an
     omission — the old wording ("No quantities were entered") read as a fault the rep should
     correct, on the path they were told to take. */
  ctx.status?.success?.(lineCount
    ? `Opening the Workstation for ${design.ref} with ${lineCount} line item${lineCount === 1 ? '' : 's'}…`
    : `Opening the Workstation for ${design.ref}. Add the line items there.`);
  window.open(WORKSTATION_URL, '_blank');
  return true;
}
